/* One buying block (weight and dose, lots of one variant sold as a real quantity,
   purchase method). Refonte of 28/09 after Balto, on Immunité; every single-variant
   product since 02/10, and products whose variants are formats since 07/10 (the
   format is chosen first, the lots are lots of that format). Per product, from the snippet: the Kaching
   offer and its bars, the unit (custom.contenance, else Kaching's) and the cards
   rendered. The lots shown stop at the last bar of the V&C grid (lastLot).
   Without the doses in a pot (product custom.doses_par_contenant: the days a pot lasts at
   one dose; no fallback on the variant since 02/10) nothing is computed: no
   course length, dose count, daily price or advised frequency, and the advised
   lot is the one the offer labels « Recommandé ».
   Lot prices come from the V&C Quantity Pricing grid (app proxy) and are computed
   exactly as its Function does; before the switch, a subscribed fixed-price lot follows
   Kaching's own rounding instead (kachingSubscribed).
   Purchase method: our own selector. The Kaching Subscriptions widget stays in the
   page, hidden by museau-bundle.css. It writes or removes the FIRST [name=selling_plan]
   INSIDE the form: ours lives outside the form (form= attribute), where it cannot
   reach it, and three guards keep its own field from leaving with the form:
   refresh() disables it (run by the observer and at submit), and the formdata
   listener rewrites selling_plan in every FormData built from the form. Exactly one
   selling_plan in subscription, none in one-time purchase.
   Who charges the lot discount is set in the snippet (data-discount-engine), never
   guessed from the proxy: a proxy down after the switch must not bring Kaching
   prices back.
   - "kaching" (until the switch): Kaching Bundles charges through the
     __kaching_bundles property, and also discounts a line WITHOUT it, by its
     quantity (measured 28/09: 1 pack Zéro Parasites at 29,90 €). So the grid is
     awaited as for "app" (the button waits); without it, one unit only.
   - "app": no Kaching property. The Function reads its own configuration, not
     the proxy: without the grid the page cannot know the lot prices. While it
     loads, the prices rendered by the snippet stay and the button waits; if it
     never comes (or the proxy does not say the discount is active, which is also
     its answer for a product left out of the grid), one pot only, whose price no
     tier changes. An active grid for this product that does not price this
     variant, currency or purchase type: catalogue price times the quantity, as
     charged. */
(() => {
  if (customElements.get('museau-bundle')) return;

  // Exact port of forge-bundles/shared/tier-pricing.ts (docs/CONTRACT-V3.md § 2).
  // Integers only, same operations in the same order: keep both files in step.
  const tierFor = (tiers, quantity) => {
    let found = null;
    for (const tier of tiers) if (tier.quantity <= quantity && (!found || tier.quantity > found.quantity)) found = tier;
    return found;
  };
  const lotPrice = (tiers, unitCents, quantity, subscriptionBaseCents) => {
    const full = unitCents * quantity;
    const tier = tierFor(tiers, quantity);
    if (!tier) return { totalCents: full, tier: null };
    if (tier.kind === 'TOTAL') {
      const oneTime = Math.floor((quantity * tier.value) / tier.quantity);
      const target = subscriptionBaseCents ? Math.floor((2 * oneTime * unitCents + subscriptionBaseCents) / (2 * subscriptionBaseCents)) : oneTime;
      return { totalCents: Math.min(full, target), tier };
    }
    const perItem = tier.kind === 'PERCENT' ? Math.floor((unitCents * tier.value) / 10000) : tier.value;
    const off = Math.max(0, Math.min(perItem, unitCents - 1));
    return { totalCents: full - off * quantity, tier };
  };

  // What Kaching charges for a subscribed lot whose bar is a fixed price (« specific »), in
  // real carts on 02/10: the lot's discount as a share of its one-time price, computed in
  // floating point, applied to the plan total and rounded. Exact halves measured: 3 388,5 →
  // 3 388 and 2 848,5 → 2 848, both even floors, so « half to even » and « half down » both
  // fit; an odd floor was never measured (none exists in today's offers, relecture C).
  // Same as lotPrice except on half cents: Krill 6 pots subscribed, 74,11 € charged where
  // lotPrice gives 74,12 € (the float share lands just above the half).
  const kachingSubscribed = (oneTimeCents, unitCents, planCents, quantity) => {
    const planTotal = planCents * quantity;
    const discount = (1 - oneTimeCents / (unitCents * quantity)) * planTotal;
    const floor = Math.floor(discount);
    const rounded = discount - floor === 0.5 ? (floor % 2 === 0 ? floor : floor + 1) : Math.round(discount);
    return planTotal - rounded;
  };
  const KINDS = ['TOTAL', 'PERCENT', 'AMOUNT'];
  const isCount = value => Number.isInteger(value) && value >= 0;
  // Delivery interval of a plan, in months: the Kaching config option first
  // ({"delivery":{"interval":"MONTH","frequency":2}}), else the plan name.
  const planMonths = allocation => {
    for (const value of allocation.options || []) {
      try {
        const delivery = JSON.parse(value)?.delivery;
        if (delivery?.interval === 'MONTH' && Number.isInteger(delivery.frequency) && delivery.frequency > 0) return delivery.frequency;
      } catch {}
    }
    const name = String(allocation.name || '');
    const count = /(\d+)\s*mois/i.exec(name);
    if (count) return Number(count[1]) || null;
    return /tous les mois|chaque mois/i.test(name) ? 1 : null;
  };

  class MuseauBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('[data-museau-bundle-pilot]');
      this.form = document.getElementById(this.dataset.formId);
      this.picker = this.querySelector('[data-bundle-picker]');
      if (!this.root || !this.form || !this.picker) return;
      const data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
      this.base = data.base;
      // Formats (07/10, Vinaigre 100 ml / 250 ml): each variant of a one-option product is
      // a format; this.base is the chosen one, and everything priced follows it.
      this.formats = Array.isArray(data.formats) && data.formats.length > 1
        ? data.formats.map(format => ({ ...format, allocations: (format.allocations || []).map(allocation => ({ ...allocation, months: planMonths(allocation) })) }))
        : null;
      if (this.formats) this.base = this.formats.find(format => String(format.id) === String(data.base?.id)) || this.formats[0];
      this.plans = data.plans || [];
      this.lotPacks = (data.packs || []).map(pack => ({ ...pack, quantity: Number(pack.quantity) }));
      this.netAmount = Number(data.netAmount) > 0 ? Number(data.netAmount) : 0;
      this.netUnit = data.netUnit === 'ml' ? 'ml' : 'g';
      // No default: an invented duration would make the cards, the course advice and the daily price lie.
      this.daysPerPot = Number(data.daysPerPot) > 0 ? Number(data.daysPerPot) : 0;
      this.doseNoun = typeof data.doseNoun === 'string' ? data.doseNoun.trim() : '';
      // Short course (vermifuge, 07/10): its length in days; the cards count whole courses.
      this.cureDays = Number(data.cureDays) > 0 ? Number(data.cureDays) : 0;
      // The dose given N days a week only (dental hygiene: 3): durations grow by 7/N.
      this.perWeek = Number(data.perWeek) >= 1 && Number(data.perWeek) <= 7 ? Number(data.perWeek) : 0;
      this.units = Array.isArray(data.units) && data.units[0] ? data.units : ['pot', 'pots'];
      // Kaching offer of this product (snippets/museau-kaching-offres.liquid): its short id and
      // its bar ids by quantity. Past the last bar, Kaching charges the per-unit price of the
      // last bar when it receives it (checked in real carts on Immunité, 5 and 6 pots).
      const bars = data.kaching && data.kaching.bars && typeof data.kaching.bars === 'object' ? data.kaching.bars : null;
      const barQuantities = bars ? Object.keys(bars).map(Number).filter(quantity => Number.isInteger(quantity) && quantity >= 1 && bars[quantity]) : [];
      this.kaching = data.kaching && typeof data.kaching.deal === 'string' && data.kaching.deal && barQuantities.length
        ? { deal: data.kaching.deal, bars, lastBar: Math.max(...barQuantities) } : null;
      this.maxQuantity = Number(this.dataset.maxQuantity) || 6;
      this.appEngine = this.dataset.discountEngine === 'app';
      this.grid = null;
      this.gridState = 'loading';
      if (!this.base) return;
      if (!this.formats) this.base.allocations = (this.base.allocations || []).map(allocation => ({ ...allocation, months: planMonths(allocation) }));
      this.applyFormatNet();
      this.quantityInput = this.form.querySelector('[name=quantity]');
      if (!this.quantityInput) {
        this.quantityInput = document.createElement('input');
        this.quantityInput.type = 'hidden';
        this.quantityInput.name = 'quantity';
        this.form.append(this.quantityInput);
      }
      this.bundlePropertyInput = this.form.querySelector('[name="properties[__kaching_bundles]"]');
      if (!this.bundlePropertyInput) {
        this.bundlePropertyInput = document.createElement('input');
        this.bundlePropertyInput.type = 'hidden';
        this.bundlePropertyInput.name = 'properties[__kaching_bundles]';
        this.form.append(this.bundlePropertyInput);
      }
      // Our selling_plan: outside the form, attached by form= (see the header).
      this.planInput = this.querySelector('[data-bundle-selling-plan]');
      this.methods = [...this.querySelectorAll('[data-bundle-method]')];
      this.subscriptionRadio = this.methods.find(radio => radio.value === 'subscription') || null;
      this.frequency = this.querySelector('[data-bundle-frequency]');
      // The advised frequency is preselected until the buyer picks one himself.
      this.frequencyChosen = false;
      const variantInput = this.form.querySelector('[name=id]');
      variantInput.value = this.base.id;
      variantInput.disabled = false;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.radios = [...this.querySelectorAll('[data-bundle-quantity]')];
      this.weights = [...this.querySelectorAll('[data-bundle-weight]')];
      this.expanded = false;
      // A deep link (?variant=: Google Shopping, Meta catalogue) keeps what it points to: one
      // pot, at the price the ad shows (Merchant Center compares the ad's price with the page's;
      // relecture C, 02/10, undoing relecture B). Otherwise the weight picks the quantity.
      this.userChose = new URLSearchParams(location.search).has('variant');
      this.money = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.weights.forEach((weight, index) => {
        weight.addEventListener('click', () => this.chooseWeight(index));
        weight.addEventListener('keydown', event => {
          let next = index;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.weights.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.weights.length) % this.weights.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.weights.length - 1;
          else if (event.key !== ' ' && event.key !== 'Enter') return;
          event.preventDefault();
          this.weights[next].focus();
          this.chooseWeight(next);
        });
      });
      this.onChange = () => {
        const button = this.form.querySelector('[name=add]');
        if (this.lastQuantity !== this.selectedQuantity() && !button?.classList.contains('loading')) {
          button?.removeAttribute('aria-disabled');
        }
        this.refresh();
      };
      this.form.addEventListener('change', this.onChange);
      // Quantity cards, purchase method and frequency all live in this element.
      this.addEventListener('change', event => {
        if (event.target.matches('[data-bundle-format]')) {
          this.chooseFormat(event.target.value);
          return;
        }
        if (event.target.matches('[data-bundle-quantity]') && event.isTrusted) this.userChose = true;
        // Nothing else fires change on the select (refresh() sets its value silently): a buyer's pick.
        if (event.target === this.frequency) {
          this.frequencyChosen = true;
          // Picking a frequency is choosing the subscription.
          if (this.subscriptionRadio && !this.subscriptionRadio.checked) this.subscriptionRadio.checked = true;
        }
        this.onChange();
      });
      // Pressing the card already checked fires no change: it is a choice all the same.
      const chose = event => {
        if (!event.isTrusted || !event.target.closest('[data-bundle-choice]')) return;
        if (event.type === 'click' || [' ', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) this.userChose = true;
      };
      this.picker.addEventListener('click', chose);
      this.picker.addEventListener('keydown', chose);
      // The whole subscription card chooses it (benefits, advice), not only its title line.
      this.subscriptionRadio?.closest('[data-bundle-method-choice]')?.addEventListener('click', event => {
        if (event.target.closest('select, label, input, a, button') || this.subscriptionRadio.checked) return;
        this.subscriptionRadio.checked = true;
        this.onChange();
      });
      this.more = this.querySelector('[data-bundle-more]');
      this.more.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.refresh();
      });
      // Native Kaching injects/removes selling_plan and changes its value attribute.
      // Observe the form and widget, never infer a discount from displayed text.
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.root.querySelector('.product__info-container') || this.root, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'checked', 'disabled', 'aria-disabled', 'class', 'quantity', 'bundle-pricing-data']
      });
      this.onSubmit = event => {
        this.refresh();
        if (!this.purchasable()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.refresh();
        }
      };
      this.form.addEventListener('submit', this.onSubmit, true);
      // Last guard: whatever builds a FormData from the form (the theme, Kaching Cart,
      // a native submit) gets our plan and only ours, even right after Kaching
      // re-injected its field, before the observer ran.
      this.onFormData = event => {
        const planId = this.selectedPlan();
        event.formData.delete('selling_plan');
        if (planId) event.formData.set('selling_plan', planId);
      };
      this.form.addEventListener('formdata', this.onFormData);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
        if (event.data.sectionId === this.dataset.section) this.refresh();
      });
      this.sticky = this.querySelector('[data-bundle-sticky]');
      this.stickyButton = this.querySelector('[data-bundle-sticky-return]');
      this.primaryButton = this.form.querySelector('[name=add]');
      if (this.sticky && this.primaryButton) {
        // Sense animates the info wrapper with transform, which would trap position:fixed.
        this.root.append(this.sticky);
        const returnToBundle = () => {
          this.picker.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
          this.picker.querySelector('input:checked')?.focus({ preventScroll: true });
        };
        this.stickyButton.addEventListener('click', returnToBundle);
        this.sticky.querySelector('[data-bundle-sticky-edit]').addEventListener('click', returnToBundle);
        // Reserve exactly the rendered bar height, including mobile safe area.
        this.stickyResizeObserver = new ResizeObserver(() => {
          if (!this.sticky.hidden) document.body.style.setProperty('--museau-sticky-height', `${this.sticky.getBoundingClientRect().height}px`);
        });
        this.stickyResizeObserver.observe(this.sticky);
        // No bar above the buying area: show it only after the primary CTA has been passed.
        // The root reaches far below the viewport, so the button "intersects" while it is
        // anywhere under the top edge: a jump past it (anchor link, End key) still flips the bar.
        this.stickyObserver = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
        }, { rootMargin: '0px 0px 100000px 0px' });
        this.stickyObserver.observe(this.primaryButton);
      }
      this.chooseWeight(0, !this.userChose);
      this.loadGrid();
      this.animateHeadings();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.stickyObserver?.disconnect();
      this.headingObserver?.disconnect();
      this.stickyResizeObserver?.disconnect();
      document.body.style.removeProperty('--museau-sticky-height');
      this.sticky?.remove();
      this.unsubscribe?.();
      this.form?.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.form?.removeEventListener('formdata', this.onFormData);
    }

    // Like Rue des Plantes: draw each decoration when it enters the viewport.
    // The default remains fully drawn if JS or motion support is unavailable.
    animateHeadings() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
      const decorations = this.querySelectorAll('.museau-bundle-heading-text > span');
      this.headingObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.dataset.decoState = 'draw';
          this.headingObserver.unobserve(entry.target);
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.3 });
      for (const decoration of decorations) {
        decoration.querySelector('path')?.setAttribute('pathLength', '100');
        decoration.dataset.decoState = 'wait';
        this.headingObserver.observe(decoration);
      }
    }

    // --- Price grid (app proxy) -------------------------------------------------
    // Money rule: a published theme shows a grid only when it is active and never
    // asks for the draft; an unpublished theme or the theme editor may show the draft.
    draftAllowed() {
      const shopify = window.Shopify || {};
      const role = shopify.theme && shopify.theme.role;
      return shopify.designMode === true || this.dataset.designMode === 'true' || (typeof role === 'string' && role !== 'main');
    }
    async fetchGrid(draft) {
      const url = new URL(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}apps/quantity-pricing`, location.origin);
      url.searchParams.set('product_id', `gid://shopify/Product/${this.dataset.productId}`);
      if (draft) url.searchParams.set('source', 'draft');
      const controller = new AbortController();
      // After the switch the button waits for this answer: give up sooner.
      const timer = setTimeout(() => controller.abort(), this.appEngine ? 4000 : 8000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) return null;
        const payload = await response.json();
        const grid = this.resolveGrid(payload, draft);
        // Kept to price another format without asking the proxy again (chooseFormat), even
        // when it does not cover the format shown first (relecture formats du 07/10, F4).
        if (grid || (this.formats && Array.isArray(payload?.offers) && payload.offers.length)) this.gridSource = { payload, draft };
        return grid;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    }
    async loadGrid() {
      const draft = this.draftAllowed();
      let grid = draft ? await this.fetchGrid(true) : null;
      if (!grid) grid = await this.fetchGrid(false);
      if (!grid && this.appEngine && this.isConnected) {
        // One more try before falling back to one pot.
        await new Promise(resolve => setTimeout(resolve, 1500));
        grid = await this.fetchGrid(false);
      }
      this.gridSettled = true;
      if (!this.isConnected) return;
      this.grid = grid;
      this.gridState = grid ? 'ready' : 'failed';
      // Newly priced quantities can change the recommendation: follow it unless the buyer chose.
      if (grid && !this.userChose) this.selectQuantity(this.recommended());
      this.refresh();
    }
    // The offer the Function applies to the base variant for one purchase type:
    // a LIST offer listing it, else an ALL offer (CONTRACT-V3 § 2, as forge-bundle.js).
    pickOffer(offers, purchaseType) {
      const currency = this.dataset.currency;
      const gid = `gid://shopify/ProductVariant/${this.base.id}`;
      const covers = offer => offer && (offer.purchaseType === 'BOTH' || offer.purchaseType === purchaseType);
      const valid = offer => offer && (!currency || offer.currencyCode === currency)
        && Array.isArray(offer.tiers) && offer.tiers.length > 0
        && offer.tiers.every(tier => Number.isInteger(tier.quantity) && tier.quantity >= 1 && KINDS.includes(tier.kind) && isCount(tier.value));
      const listed = offers.find(offer => covers(offer) && offer.scope !== 'ALL' && Array.isArray(offer.eligibleVariantIds) && offer.eligibleVariantIds.includes(gid));
      if (listed) {
        // Lot totals were set for the catalogue price: at another price the grid no longer
        // describes what Kaching charges. The Function still applies it, and lotPrice with
        // the offer's base price is its mirror.
        if (!this.appEngine && listed.tiers?.some(tier => tier.kind === 'TOTAL') && listed.baseUnitPriceCents !== this.base.price) return null;
        return valid(listed) ? listed : null;
      }
      const all = offers.find(offer => covers(offer) && offer.scope === 'ALL');
      return valid(all) ? all : null;
    }
    resolveGrid(payload, draft) {
      if (!payload || !Array.isArray(payload.offers)) return null;
      const usable = payload.active === true || (draft && this.draftAllowed() && payload.draft === true);
      if (!usable) return null;
      const oneTime = this.pickOffer(payload.offers, 'ONE_TIME');
      const subscription = this.base.allocations.length ? this.pickOffer(payload.offers, 'SUBSCRIPTION') : null;
      // Kaching needs every price at once: a grid missing one purchase type is not used.
      // The app prices a purchase type its offer skips at the catalogue price (lotCents).
      if (!this.appEngine && (!oneTime || (this.base.allocations.length && !subscription))) return null;
      // Kaching discounts a lot only when told which offer and bar: without them, lot prices
      // the cart would not charge. One unit, at the catalogue price.
      if (!this.appEngine && !this.kaching) return null;
      // Active and nothing covers this variant: the Function does not discount it (lotCents).
      if (this.appEngine && !oneTime && !subscription && payload.active !== true) return null;
      return { oneTime, subscription };
    }

    // --- Formats -----------------------------------------------------------------
    // The snippet's lots before the grid: its variants by quantity, or one unit of the
    // chosen format.
    get packs() {
      return this.formats ? [{ id: this.base.id, quantity: 1, price: this.base.price }] : this.lotPacks;
    }
    // Net content of one unit of the format, read in its title (« 250ml » → 250 ml).
    applyFormatNet() {
      if (!this.formats) return;
      const match = /^\s*(\d+(?:[.,]\d+)?)\s*(ml|g)\s*$/i.exec(String(this.base.title || ''));
      this.netAmount = match ? Number(match[1].replace(',', '.')) : 0;
      this.netUnit = match && match[2].toLowerCase() === 'g' ? 'g' : 'ml';
    }
    // The frequency list is the served format's plans (snippet). Another format with other
    // plan ids gets its own list, the buyer's plan kept when it still exists, else the
    // first: never a plan of the other variant (relecture formats du 07/10, F1).
    syncFrequencyOptions() {
      if (!this.frequency) return;
      const ids = this.base.allocations.map(allocation => String(allocation.id));
      const current = [...this.frequency.options].map(option => option.value);
      if (ids.length && JSON.stringify(ids) === JSON.stringify(current)) return;
      const kept = ids.includes(this.frequency.value) ? this.frequency.value : ids[0] || '';
      this.frequency.replaceChildren(...this.base.allocations.map(allocation => new Option(allocation.name, allocation.id)));
      this.frequency.value = kept;
      if (!kept) this.frequencyChosen = false;
    }
    formatLabel() {
      return this.formats ? String(this.base.title || '').replace(/(\d)\s*(ml|g)\b/i, '$1 $2') : '';
    }
    // A new format: its id goes to the cart, its prices and plans to the cards, and the grid
    // already read is resolved again for its variant. The lot is priced as one format: a
    // cart holding both formats is counted together, by product, by Kaching AND by the
    // Function (CONTRACT-V3 § 1.3, Vinaigre 100 + 250 ml = 36,02 € for 39,90 € shown), so
    // it pays less than the two lots shown, never more (relecture formats du 07/10, M1).
    chooseFormat(id) {
      const format = this.formats?.find(item => String(item.id) === String(id));
      if (!format || format === this.base) return;
      this.base = format;
      this.applyFormatNet();
      this.syncFrequencyOptions();
      const variantInput = this.form.querySelector('[name=id]');
      if (variantInput.value !== String(format.id)) {
        variantInput.value = format.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.gridSource) {
        this.grid = this.resolveGrid(this.gridSource.payload, this.gridSource.draft);
        this.gridState = this.grid ? 'ready' : 'failed';
      }
      this.expanded = false;
      if (!this.userChose) {
        const recommended = this.recommended();
        if (recommended) this.selectQuantity(recommended);
      }
      this.refresh();
    }

    // --- Prices ------------------------------------------------------------------
    // The largest lot sold: the last bar of the V&C grid (Valentin, 06/10: « je préfère
    // avoir les lots 5 et 6 dans l'admin V&C »; the page sells what the bundle lists),
    // never above the cards rendered (data-max-quantity: before the switch, the lots
    // Kaching was seen charging; after it, the block's six cards). A grid with no offer
    // for this variant (app, active): the cards rendered, at the catalogue price.
    lastLot() {
      if (!this.grid) return this.maxQuantity;
      const offers = [this.grid.oneTime, this.grid.subscription].filter(Boolean);
      const last = Math.max(0, ...offers.flatMap(offer => [Number(offer.baseQuantity) || 0, ...offer.tiers.map(tier => tier.quantity)]));
      return last >= 1 ? Math.min(this.maxQuantity, last) : this.maxQuantity;
    }
    quantities() {
      if (this.grid) return Array.from({ length: this.lastLot() }, (_, index) => index + 1);
      if (this.appEngine && this.gridState === 'failed') return [1];
      return this.packs.map(pack => pack.quantity).filter(quantity => quantity >= 1 && quantity <= this.maxQuantity).sort((a, b) => a - b);
    }
    allocation(planId) {
      return planId ? this.base.allocations.find(item => String(item.id) === String(planId)) : null;
    }
    planDetails(planId) {
      const plan = this.plans.find(item => String(item.id) === String(planId));
      return plan?.priceAdjustment?.type === 'percentage' && Number.isFinite(plan.priceAdjustment.value) ? plan : null;
    }
    // Price of a lot, in cents, exactly as charged; null when nobody gave it.
    lotCents(quantity, planId) {
      const allocation = this.allocation(planId);
      if (planId && !allocation) return null;
      const offer = this.grid && (planId ? this.grid.subscription : this.grid.oneTime);
      if (offer) {
        if (quantity < 1 || quantity > this.lastLot()) return null;
        const lot = lotPrice(offer.tiers, allocation ? allocation.price : this.base.price, quantity, allocation ? offer.baseUnitPriceCents : undefined);
        // Before the switch Kaching charges, with its own rounding (kachingSubscribed).
        if (!this.appEngine && allocation && lot.tier?.kind === 'TOTAL') {
          return kachingSubscribed(lotPrice(offer.tiers, this.base.price, quantity).totalCents, this.base.price, allocation.price, quantity);
        }
        return lot.totalCents;
      }
      const catalogue = (allocation ? allocation.price : this.base.price) * quantity;
      if (this.appEngine) {
        // Active grid, no offer for this lot: the Function does not discount it.
        if (this.grid) return quantity >= 1 && quantity <= this.lastLot() ? catalogue : null;
        // No grid: one unit at the catalogue price. True where no tier starts at 1; the packs'
        // offers do (one pack 29,90 € for 34,90 €), and there the Function charges LESS than
        // shown. Accepted on 02/10 rather than refusing to sell a pack while the proxy is down.
        if (this.gridState === 'failed') return quantity === 1 ? catalogue : null;
      } else if (this.grid) return null;
      const pack = this.packs.find(item => item.quantity === quantity);
      if (!pack) return null;
      return allocation ? Math.round(pack.price * allocation.price / this.base.price) : pack.price;
    }
    fullCents(quantity) {
      return this.base.price * quantity;
    }

    // --- Dose, durations, recommendation ----------------------------------------
    selectedWeight() {
      return this.weights.find(weight => weight.classList.contains('selected'));
    }
    dose() {
      return Number(this.selectedWeight()?.dataset.quantity) || 0;
    }
    // Days a lot lasts at the dose, every day or N days a week. A short course has no
    // continuous duration: see cures().
    days(quantity) {
      const dose = this.dose();
      if (this.cureDays || !(dose > 0)) return 0;
      return quantity * this.daysPerPot / (dose * (this.perWeek ? this.perWeek / 7 : 1));
    }
    courseMode() {
      return Boolean(this.cureDays && this.dose() > 0 && this.daysPerPot > 0);
    }
    // Whole courses in a lot: 20 tablets at 3 a day for 7 days (21) is not one.
    cures(quantity) {
      return this.courseMode() ? Math.floor(quantity * this.daysPerPot / (this.dose() * this.cureDays)) : 0;
    }
    lotDuration(quantity) {
      if (!this.courseMode()) return this.duration(this.days(quantity));
      const count = this.cures(quantity);
      return count >= 1 ? `${count} cure${count > 1 ? 's' : ''} de ${this.cureDays} jours` : 'moins d’une cure';
    }
    duration(days) {
      if (!(days > 0)) return '';
      const months = Math.round(days / 30);
      if (months >= 1 && Math.abs(days / 30 - months) < 0.05) return `environ ${months} mois`;
      if (days < 14) return `environ ${Math.round(days)} jours`;
      if (days < 84) return `environ ${Math.round(days / 7)} semaines`;
      return `environ ${months} mois`;
    }
    canBuy(quantity) {
      if (!this.base.available) return false;
      return !this.base.management || this.base.policy === 'continue' || Number(this.base.quantity) >= quantity;
    }
    // At least two pots and a course of at least 60 days at the chosen dose. Without a dose or
    // a pot duration: the lot the offer labels « Recommandé », as Kaching shows it, if buyable.
    recommended() {
      const quantities = this.quantities();
      if (!quantities.length) return null;
      const buyable = quantities.filter(quantity => this.canBuy(quantity));
      const pool = buyable.length ? buyable : quantities;
      // A short course: the lot the offer recommends if it holds a whole course at the
      // dose, else the smallest that does.
      if (this.courseMode()) {
        const labelled = this.grid?.oneTime?.tiers?.find(tier => /recommand/i.test(String(tier.label || '')))?.quantity;
        if (buyable.includes(labelled) && this.cures(labelled) >= 1) return labelled;
        return pool.find(quantity => this.cures(quantity) >= 1) || pool[pool.length - 1];
      }
      if (this.dose() > 0 && this.daysPerPot > 0) return pool.find(quantity => quantity >= 2 && this.days(quantity) >= 60) || pool[pool.length - 1];
      const labelled = this.grid?.oneTime?.tiers?.find(tier => /recommand/i.test(String(tier.label || '')))?.quantity;
      return buyable.includes(labelled) ? labelled : null;
    }
    // The three cards shown folded (Valentin, 28/09): the entry lot (the smallest
    // that lasts about three weeks at the dose), the recommended course in the
    // middle, one pot more. Nothing above the recommendation: the lot halfway
    // between the entry and it, the recommendation last. The chosen lot is added.
    featured(quantities, recommended) {
      // Three lots or fewer (packs, sprays): all of them, whatever the weight (Valentin, 09/10:
      // « on a que 2 et 3 packs à recommander […] autant garder le premier bundle »).
      if (quantities.length <= 3) return quantities;
      // A short course has no duration to pick an entry lot by: the first three, as without doses.
      if (recommended == null || !(this.dose() > 0) || !(this.daysPerPot > 0) || this.courseMode()) return quantities.slice(0, 3);
      const entry = Math.min(quantities.find(quantity => this.days(quantity) >= 20) ?? recommended, recommended);
      const above = quantities.find(quantity => quantity > recommended);
      if (above !== undefined) return [...new Set([entry, recommended, above])];
      const half = (entry + recommended) / 2;
      const middle = quantities.filter(quantity => quantity > entry && quantity < recommended)
        .reduce((best, quantity) => best === undefined || Math.abs(quantity - half) < Math.abs(best - half) ? quantity : best, undefined);
      return [entry, middle, recommended].filter(quantity => quantity !== undefined);
    }
    // Advised delivery frequency: how long the lot lasts at the dose, rounded to the
    // month, kept within 1 to 6 months, then the plan closest to it.
    advisedPlan(quantity) {
      const days = this.days(quantity);
      if (!(days > 0)) return null;
      const months = Math.min(6, Math.max(1, Math.round(days / 30)));
      let best = null;
      for (const allocation of this.base.allocations) {
        if (allocation.months == null) continue;
        if (!best || Math.abs(allocation.months - months) < Math.abs(best.months - months)) best = allocation;
      }
      return best;
    }
    selectedQuantity() {
      return Number(this.picker.querySelector('[data-bundle-quantity]:checked')?.value) || 0;
    }
    selectQuantity(quantity) {
      const radio = this.radios.find(item => Number(item.value) === quantity);
      if (!radio || radio.checked) return;
      radio.disabled = false;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    chooseWeight(index, chooseQuantity = true) {
      this.weights.forEach((weight, i) => {
        weight.classList.toggle('selected', i === index);
        weight.setAttribute('aria-checked', String(i === index));
        weight.tabIndex = i === index ? 0 : -1;
      });
      // The veterinary note follows the selected weight and daily dose.
      const dose = this.selectedWeight()?.dataset.dose?.trim() || '';
      const weightRange = this.selectedWeight()?.querySelector('.museau-bundle-weight-label')?.textContent.trim() || '';
      this.setText(this.querySelector('[data-bundle-weight-range]'), weightRange);
      this.setText(this.querySelector('[data-bundle-dose]'), dose);
      this.setHidden(this.querySelector('[data-bundle-dose-bar]'), !dose);
      this.querySelectorAll('[data-bundle-dose-icon]').forEach((icon, i) => this.setHidden(icon, i >= Math.round(this.dose())));
      this.expanded = false;
      if (chooseQuantity) {
        // A new weight is a new question: the recommendation answers it again.
        this.userChose = false;
        const recommended = this.recommended();
        if (recommended) this.selectQuantity(recommended);
      }
      this.refresh();
    }

    // --- Kaching -----------------------------------------------------------------
    syncBundleProperty(quantity, planId) {
      // After the switch Kaching must not be asked for a discount the app also gives.
      // Guarded writes: the observer watches `disabled`, an unconditional write loops.
      if (this.appEngine) {
        if (!this.bundlePropertyInput.disabled) this.bundlePropertyInput.disabled = true;
        if (this.bundlePropertyInput.value) this.bundlePropertyInput.value = '';
        return;
      }
      // No offer for this product: no property, and no lot either (resolveGrid). No grid either:
      // no bar is claimed for a price the page did not read. Kaching still discounts the line by
      // its quantity (the packs' bar 1: 29,90 € charged for 34,90 € shown, relecture of 02/10),
      // so the button waits for the grid (loadingPrices) and, if it never comes, the buyer pays
      // less than shown on one pack, never more (banc-fixture § 21).
      if (!this.kaching || !this.grid) {
        if (!this.bundlePropertyInput.disabled) this.bundlePropertyInput.disabled = true;
        if (this.bundlePropertyInput.value) this.bundlePropertyInput.value = '';
        return;
      }
      if (this.bundlePropertyInput.disabled) this.bundlePropertyInput.disabled = false;
      const property = { deal: this.kaching.deal, pp: Number(this.dataset.productId), main: true, bid: this.kaching.bars[Math.min(quantity, this.kaching.lastBar)] };
      const plan = this.planDetails(planId);
      if (plan) property.sellingPlan = { id: plan.id, priceAdjustment: plan.priceAdjustment };
      const value = JSON.stringify(property);
      if (this.bundlePropertyInput.value !== value) this.bundlePropertyInput.value = value;
    }
    // The hidden widget keeps the lot's prices: harmless, and right if it ever shows.
    syncNativeWidget(quantity) {
      const oneTime = this.lotCents(quantity, '');
      if (oneTime == null) return;
      const payload = JSON.stringify({
        fullPrice: { amount: oneTime / 100 },
        discountedPriceWithoutSellingPlan: { amount: oneTime / 100 },
        discountedPricesForSellingPlans: this.base.allocations.map(allocation => ({
          sellingPlanId: allocation.id,
          amount: (this.lotCents(quantity, allocation.id) ?? oneTime) / 100
        }))
      });
      this.root.querySelectorAll('kaching-subscriptions-block').forEach(widget => {
        if (widget.getAttribute('current-variant-id') !== String(this.base.id)) widget.setAttribute('current-variant-id', this.base.id);
        if (widget.getAttribute('quantity') !== String(quantity)) widget.setAttribute('quantity', quantity);
        if (widget.getAttribute('bundle-pricing-data') !== payload) widget.setAttribute('bundle-pricing-data', payload);
      });
    }
    // Every other selling_plan field of the form (Kaching's, re-injected or not):
    // disabled, so it never leaves with the form. Guarded writes (observer).
    neutralizeForeignPlans() {
      const fields = new Set([...this.form.querySelectorAll('[name="selling_plan"]'), ...[...this.form.elements].filter(field => field.name === 'selling_plan')]);
      fields.forEach(field => {
        if (field === this.planInput) return;
        if (!field.disabled) field.disabled = true;
        if (!field.hasAttribute('data-museau-neutralized')) field.setAttribute('data-museau-neutralized', '');
      });
    }
    syncPlanInput(planId) {
      if (!this.planInput) return;
      if (this.planInput.value !== planId) this.planInput.value = planId;
      if (this.planInput.disabled !== !planId) this.planInput.disabled = !planId;
    }
    subscribed() {
      return Boolean(this.subscriptionRadio?.checked && this.frequency);
    }
    // The plan the cart receives: the frequency, in subscription only.
    selectedPlan() {
      return this.subscribed() ? this.frequency.value : '';
    }
    // The snippet's prices are only a placeholder until the grid: after the switch, and
    // before it too when the product has a Kaching offer (it discounts by quantity alone).
    loadingPrices() {
      return this.gridState === 'loading' && (this.appEngine || Boolean(this.kaching));
    }
    // Everything the cart will receive agrees with what is displayed.
    purchasable() {
      const quantity = this.selectedQuantity();
      const planId = this.selectedPlan();
      return quantity > 0 && this.quantities().includes(quantity) && this.canBuy(quantity)
        && this.lotCents(quantity, planId) != null
        // Kaching needs a percentage plan in its property; the Function takes any plan.
        && (!planId || this.appEngine || Boolean(this.planDetails(planId)))
        && !this.loadingPrices()
        && String(this.base.id) === this.form.querySelector('[name=id]').value
        && Number(this.quantityInput.value) === quantity;
    }

    // --- Rendering ---------------------------------------------------------------
    setText(element, value) {
      if (element && element.textContent !== value) element.textContent = value;
    }
    setHidden(element, hidden) {
      if (element && element.hidden !== hidden) element.hidden = hidden;
    }
    format(cents) {
      return this.money.format(cents / 100);
    }
    quantityLabel(quantity) {
      const format = this.formatLabel();
      return `${quantity} ${quantity > 1 ? this.units[1] || this.units[0] : this.units[0]}${format ? ` de ${format}` : ''}`;
    }
    // A price and its crossed-out catalogue price, shown only when higher.
    setPrice(price, compare, compareValue, lot, full) {
      this.setText(price, lot == null ? '' : this.format(lot));
      this.setText(compareValue, this.format(full));
      this.setHidden(compare, !(lot != null && full > lot));
    }
    refresh() {
      const quantities = this.quantities();
      let quantity = this.selectedQuantity();
      if (!quantities.includes(quantity)) {
        // A grid that disappears, or arrives with its last bar below the checked lot
        // (lastLot): back to a priced quantity.
        const fallback = this.recommended() || quantities[0];
        if (!fallback) return;
        this.selectQuantity(fallback);
        quantity = fallback;
      }
      if (this.quantityInput.value !== String(quantity)) {
        this.quantityInput.value = quantity;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      // The advised frequency follows the lot and the weight until the buyer picks one.
      const advised = this.advisedPlan(quantity);
      if (this.frequency && advised && !this.frequencyChosen && this.frequency.value !== String(advised.id)) this.frequency.value = String(advised.id);
      const planId = this.selectedPlan();
      const subscribed = Boolean(planId && this.allocation(planId));
      this.neutralizeForeignPlans();
      this.syncPlanInput(planId);
      this.syncNativeWidget(quantity);
      this.syncBundleProperty(quantity, planId);
      const recommended = this.recommended();
      // Net content is distinct from Shopify's shipping weight. All cost indicators
      // use the current lot total; daily cost uses unrounded coverage in days.
      const selectedLot = this.lotCents(quantity, planId);
      const selectedDays = this.days(quantity);
      const totalNet = this.netAmount > 0 ? this.netAmount * quantity : 0;
      // daysPerPot is the pot coverage at one dose per day, so it is also the number of
      // doses in a pot. Shown only with a dose name the snippet could trust.
      const doseCount = this.doseNoun && this.daysPerPot > 0 ? this.daysPerPot * quantity : 0;
      this.setText(this.querySelector('[data-bundle-spoons]'), doseCount > 0 ? `${new Intl.NumberFormat('fr-FR').format(doseCount)} ${this.doseNoun}` : '');
      this.setHidden(this.querySelector('[data-bundle-spoons]'), !(doseCount > 0));
      this.setText(this.querySelector('[data-bundle-mass]'), totalNet > 0 ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(totalNet)} ${this.netUnit}` : '');
      this.setHidden(this.querySelector('[data-bundle-mass]'), !(totalNet > 0));
      this.setText(this.querySelector('[data-bundle-lot-context]'), subscribed ? 'Lot sélectionné, prix par livraison : ' : 'Lot sélectionné, achat unique : ');
      const daily = this.querySelector('[data-bundle-daily]');
      this.setText(daily, selectedDays > 0 && selectedLot != null ? `${this.format(Math.round(selectedLot / selectedDays))}/jour` : '');
      this.setHidden(daily, !(selectedDays > 0 && selectedLot != null));
      const visible = new Set([...this.featured(quantities, recommended), quantity]);
      const collapsible = quantities.some(item => !visible.has(item));
      this.radios.forEach(radio => {
        const item = Number(radio.value);
        const choice = radio.closest('[data-bundle-choice]');
        const priced = quantities.includes(item);
        const lot = priced ? this.lotCents(item, planId) : null;
        const shown = priced && lot != null && (this.expanded || !collapsible || visible.has(item));
        if (radio.disabled === priced) radio.disabled = !priced;
        this.setHidden(choice, !shown);
        if (lot == null) return;
        const full = this.fullCents(item);
        const saving = full > lot ? Math.floor(((full - lot) * 100) / full) : 0;
        // Rounded down, as in the snippet: 4, 5 and 6 pots show the same price per pot.
        this.setText(choice.querySelector('[data-bundle-total]'), this.format(lot));
        this.setText(choice.querySelector('[data-bundle-unit]'), `${this.format(Math.floor(lot / item))}/${this.units[0]}`);
        this.setText(choice.querySelector('[data-bundle-saving-value]'), `−${saving} %`);
        this.setHidden(choice.querySelector('[data-bundle-saving]'), !(saving > 0));
        this.setText(choice.querySelector('[data-bundle-duration]'), this.lotDuration(item));
        // A lone lot is not a recommendation (prices not loaded: one unit only).
        this.setHidden(choice.querySelector('[data-bundle-recommended]'), item !== recommended || quantities.length < 2);
        this.setHidden(choice.querySelector('[data-bundle-unavailable]'), this.canBuy(item));
      });
      this.setHidden(this.more, !collapsible);
      this.more.setAttribute('aria-expanded', String(this.expanded));
      this.setText(this.more, this.expanded ? "Voir moins d’options" : "Voir plus d’options");
      const many = this.units[1] || this.units[0];
      const failed = (this.appEngine || this.kaching) && this.gridState === 'failed' ? `lots de plusieurs ${many} momentanément indisponibles` : '';
      this.setText(this.querySelector('[data-bundle-notice]'), failed ? `Les lots de plusieurs ${many} sont momentanément indisponibles.` : '');
      this.setHidden(this.querySelector('[data-bundle-notice]'), !failed);
      // Purchase method: each line carries the lot's total, as charged.
      const full = this.fullCents(quantity);
      this.setPrice(this.querySelector('[data-bundle-once-price]'), this.querySelector('[data-bundle-once-compare]'), this.querySelector('[data-bundle-once-compare-price]'), this.lotCents(quantity, ''), full);
      if (this.frequency) {
        const frequencyPlan = this.frequency.value;
        this.setPrice(this.querySelector('[data-bundle-subscription-price]'), this.querySelector('[data-bundle-subscription-compare]'), this.querySelector('[data-bundle-subscription-compare-price]'), this.lotCents(quantity, frequencyPlan), full);
        const adjustment = this.planDetails(frequencyPlan)?.priceAdjustment.value;
        this.setText(this.querySelector('[data-bundle-plan-saving]'), adjustment > 0 ? `−${adjustment} %` : '');
        this.setHidden(this.querySelector('[data-bundle-plan-saving]'), !(adjustment > 0));
        const every = advised && (advised.months === 1 ? 'tous les mois' : `tous les ${advised.months} mois`);
        const advice = advised && String(frequencyPlan) !== String(advised.id)
          ? `Pour ce lot, une livraison ${every} est conseillée.` : '';
        this.setText(this.querySelector('[data-bundle-advice]'), advice);
        this.setHidden(this.querySelector('[data-bundle-advice]'), !advice);
      }
      const price = this.lotCents(quantity, planId);
      // For screen readers only: the chosen lot in one sentence.
      const summary = [this.quantityLabel(quantity), this.lotDuration(quantity), price != null ? this.format(price) : '', subscribed ? 'en abonnement' : 'achat unique', failed].filter(Boolean).join(' · ');
      this.setText(this.querySelector('[data-bundle-summary]'), summary);
      const button = this.form.querySelector('[name=add]');
      const busy = button?.classList.contains('loading');
      const unavailable = !this.purchasable() || price == null || (!busy && button?.getAttribute('aria-disabled') === 'true');
      this.lastQuantity = quantity;
      const error = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const message = error && !error.hidden ? error.textContent.trim() : '';
      if (message && message !== this.lastError && this.sticky && !this.sticky.hidden) {
        error.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
      this.lastError = message;
      if (button && !busy) {
        if (button.disabled !== unavailable) button.disabled = unavailable;
        this.setText(button.querySelector('span'), this.loadingPrices() ? 'Chargement des prix…' : unavailable ? 'Indisponible' : `Ajouter au panier · ${this.format(price)}`);
      }
      if (this.stickyButton) {
        this.setText(this.sticky.querySelector('[data-bundle-sticky-selection]'), `${this.quantityLabel(quantity)} · ${subscribed ? 'Abonnement' : 'Achat unique'}`);
        this.setText(this.sticky.querySelector('[data-bundle-sticky-price]'), price == null ? '' : this.format(price));
      }
    }
  }
  customElements.define('museau-bundle', MuseauBundle);
})();
